import { Editor } from 'components/Editor/Editor'
import WebEditorHeader from 'components/Header'
import { Projects } from 'components/Projects/Projects'
import React, { useEffect, useRef } from 'react'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import SplitPane from 'react-split-pane'
import { useSelector, useDispatch } from 'react-redux'
import { useDropTargetLocation } from 'hooks/useDnd'
import * as Actions from '~/actions'
import { AppRoutes } from '~/AppRoutes'
import { OfflineModePlug } from '~/components/OfflineModePlug/OfflineModePlug'
import * as LatestProjectId from '~/cookieServices/LatestProjectId'
import { useAction } from '~/hooks'
import { ModalView } from '~/views/ModalView'
import { DROP_TARGET_TYPE } from '~/enums'
import { START_PAGE } from '~/constant'
import { useNWJsFocus } from '~/hooks/nwjs/useNWJsFocus'
import { DialogView } from '~/views/DialogView'
import { MobileImport } from '~/components/MobileImport'
import { AdminPanel } from '~/components/admin/AdminPanel'
import { selectViewSettings } from '~/selectors/mainView'
import { selectActiveProjectId, selectActiveProjectLoading } from '~/selectors/projectData'
import { setEditorSettings, showSaveSettings } from '~/actions/mainView'
import { importMediafileFromUrl } from '~/actions/importFromUrl'
import { clearTimeline, rewind } from '~/actions/timeline'
import { createMediaFile } from '~/actions/sourceFiles'
import { addSourceFileAtSliderPosition } from '~/actions/addSourceFileAtSliderPosition'
import { EDITOR_MAIN_SETTINGS } from '~/config/constants/mainView'
import { ViewSettings } from '~/config/constants/editorSettings'

const Router = () => {
  const { pathname } = useLocation()
  const latestProjectId = LatestProjectId.get()

  return (
    <Switch>
      {/* Remove trailing slash */}
      <Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} />

      <Route exact path={AppRoutes.Projects}>
        <Projects />
      </Route>
      <Route exact path={AppRoutes.MobileImport}>
        <MobileImport />
      </Route>
      <Route path={AppRoutes.AdminPanel}>
        <AdminPanel />
      </Route>
      <Route path={`${AppRoutes.Projects}/:id`}>
        <Editor />
      </Route>
      <If condition={__APP_PROFILE__ === 'package'}>
        <Route path="">
          <Editor />
        </Route>
      </If>
      <If condition={![ 'package', 'itochu' ].includes(__APP_PROFILE__)}>
        <Redirect to={latestProjectId ? `${AppRoutes.Projects}/${latestProjectId}` : AppRoutes.NewProject} />
      </If>
      <If condition={__CFG__.START_PAGE === START_PAGE.PROJECTS}>
        <Redirect to={AppRoutes.Projects} />
      </If>
    </Switch>
  )
}

function MainView() {
  const loadProject = useAction(Actions.loadProject.loadProject)

  const [ , drop ] = useDropTargetLocation({ location: DROP_TARGET_TYPE.ROOT, shallow: true })

  const location = useLocation()

  const latestProjectId = LatestProjectId.get()

  const dropRef = useRef()
  useNWJsFocus()

  const dispatch = useDispatch()
  const viewSettings = useSelector(selectViewSettings)
  const headerVisible = viewSettings?.[ViewSettings.showHeader] ?? 1
  const activeProjectId = useSelector(selectActiveProjectId)
  const activeProjectLoading = useSelector(selectActiveProjectLoading)

  const importFromUrl = useAction(importMediafileFromUrl)
  const closeSaveSettings = useAction(showSaveSettings, false)
  const clearTimelineAction = useAction(clearTimeline)
  const createMediaFileAction = useAction(createMediaFile)
  const rewindAction = useAction(rewind)
  const addSourceFileAtSliderPositionAction = useAction(addSourceFileAtSliderPosition)

  const doImportUrl = async url => {
    await clearTimelineAction()
    closeSaveSettings()
    importFromUrl(url)
  }

  const doImportUrlRef = useRef(doImportUrl)
  doImportUrlRef.current = doImportUrl
  const importParamRef = useRef(new URLSearchParams(window.location.search).get('import'))
  const importStartedRef = useRef(false)

  useEffect(() => {
    const importByUrl = e => {
      doImportUrl(e.detail.videoUrl)
    }
    document.addEventListener('solveig-importFromUrl', importByUrl)
    return () => document.removeEventListener('solveig-importFromUrl', importByUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!importParamRef.current || importStartedRef.current) return
    if (activeProjectId == null || activeProjectLoading) return
    importStartedRef.current = true
    doImportUrlRef.current(decodeURIComponent(importParamRef.current))
  }, [ activeProjectId, activeProjectLoading ])

  useEffect(() => {
    const onHeaderVisible = e => {
      dispatch(setEditorSettings(EDITOR_MAIN_SETTINGS.VIEW, {
        ...viewSettings,
        [ViewSettings.showHeader]: e.detail?.visible ? 1 : 0,
      }))
    }
    document.addEventListener('solveig-header-visible', onHeaderVisible)
    return () => document.removeEventListener('solveig-header-visible', onHeaderVisible)
  }, [ viewSettings, dispatch ])

  useEffect(() => {
    const handleCepImport = event => {
      const { fileId } = event.detail || {}
      if (!fileId) return
      createMediaFileAction(fileId)
    }

    const handleCepOpenProject = event => {
      const { projectId } = event.detail || {}
      if (!projectId) return
      loadProject(projectId)
    }

    const handleCepMoveSlider = event => {
      const { position } = event.detail || {}
      if (position === undefined) return
      rewindAction(position)
    }

    const handleCepAddClipToTimeline = event => {
      const { fileId } = event.detail || {}
      if (!fileId) return
      addSourceFileAtSliderPositionAction(fileId)
    }

    window.addEventListener('veExt.importFile', handleCepImport)
    window.addEventListener('veExt.openProject', handleCepOpenProject)
    window.addEventListener('veExt.moveSlider', handleCepMoveSlider)
    window.addEventListener('veExt.addClipToTimeline', handleCepAddClipToTimeline)
    return () => {
      window.removeEventListener('veExt.importFile', handleCepImport)
      window.removeEventListener('veExt.openProject', handleCepOpenProject)
      window.removeEventListener('veExt.moveSlider', handleCepMoveSlider)
      window.removeEventListener('veExt.addClipToTimeline', handleCepAddClipToTimeline)
    }
  }, [ createMediaFileAction, loadProject, rewindAction, addSourceFileAtSliderPositionAction ])

  useEffect(() => {
    if (latestProjectId && location.pathname === AppRoutes.Projects) {
      loadProject(latestProjectId)
    }
    if (dropRef?.current?.splitPane) {
      drop(dropRef.current.splitPane)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ loadProject ])

  return (
    <>
      <OfflineModePlug />
      <If condition={headerVisible}>
        <SplitPane split="horizontal" defaultSize="48px" resizerStyle={{ display: 'none' }} ref={dropRef}>
          <WebEditorHeader />
          <Router />
        </SplitPane>
      </If>
      <If condition={!headerVisible}>
        <Router />
      </If>
      <ModalView />
      <DialogView />
    </>
  )
}

export default MainView
